import * as React from 'react';
import {useSelector} from 'react-redux';

import {SavedFilters} from 'nutshell-core/saved-filters';
import type {SavedFilter, SavedReportType} from 'nutshell-core/types';

import {useTrackReportView} from './graphql/hooks/use-track-report-view';
import {shouldShowReportDownloadAndSave} from './report-helpers';
import type {ReportKey} from './report-types';
import {isReportDirty} from './report-utils';

type Props = {
    children: React.ReactNode;
    reportKey: ReportKey;
    pathname: string;
    search: string;
    urlParams: any;
};

/**
 * Gets the value to track for a report view.
 *
 * Returns either:
 * - A saved filter ID if viewing a non-dirty saved filter
 * - A URL if viewing an unsaved report or a dirty saved filter
 */
function getTrackingValue(
    reportKey: ReportKey,
    pathname: string,
    search: string,
    urlParams: any,
    savedReport: SavedFilter<SavedReportType> | null | undefined,
    canSave: boolean
): string {
    // If this report type can't be saved, just use the URL
    if (!canSave) {
        return pathname + search;
    }

    // If we have a saved report and it's not dirty, use the filter ID
    if (savedReport && savedReport.id) {
        const isDirty = isReportDirty(reportKey, urlParams, savedReport) && !savedReport.isHidden;
        if (!isDirty) {
            return savedReport.id;
        }
    }

    // Otherwise use the URL
    return pathname + search;
}

/**
 * Tracks report views for the "recently viewed" feature on the report home page.
 *
 * Behavior:
 * - On mount: Creates a new recently viewed entry
 * - On unmount: Updates the entry with the final state (after all filter changes)
 *
 * If viewing a saved filter that hasn't been modified (not dirty), we store the
 * filter ID instead of the URL so the name is preserved.
 */
export function ReportViewTracker(props: Props): React.ReactElement {
    const {children, reportKey, pathname, search, urlParams} = props;
    const {trackReportView} = useTrackReportView();

    // Get savedReport from Redux
    const reportId = urlParams.report ? urlParams.report.id : undefined;
    const savedReport = useSelector((state) => {
        if (!reportId) {
            return undefined;
        }
        // @ts-expect-error
        const savedReports = SavedFilters.getSavedReports(state);

        return savedReports[reportId];
    });

    // Check if this report type supports saving
    const canSave = shouldShowReportDownloadAndSave(reportKey);

    // Keep track of the current tracking value so we can capture the final state on unmount
    const currentValueRef = React.useRef<string>(
        getTrackingValue(reportKey, pathname, search, urlParams, savedReport, canSave)
    );

    // Track whether we've created the initial entry
    const hasTrackedMountRef = React.useRef<boolean>(false);

    // Update the ref whenever relevant props change
    React.useEffect(() => {
        currentValueRef.current = getTrackingValue(
            reportKey,
            pathname,
            search,
            urlParams,
            savedReport,
            canSave
        );
    }, [reportKey, pathname, search, urlParams, savedReport, canSave]);

    // On mount: create a new entry
    // On unmount: update the entry with the final state
    React.useEffect(() => {
        // Only track if we haven't already (handles strict mode double-mounting)
        if (!hasTrackedMountRef.current) {
            hasTrackedMountRef.current = true;
            const initialValue = getTrackingValue(
                reportKey,
                pathname,
                search,
                urlParams,
                savedReport,
                canSave
            );
            trackReportView({url: initialValue, updateMostRecent: false});
        }

        // Cleanup function runs on unmount
        return () => {
            // Update with the final value when leaving
            trackReportView({url: currentValueRef.current, updateMostRecent: true});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportKey]); // Re-run when reportKey changes (switching between report types)

    // @ts-expect-error
    return children;
}
